import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { Radio } from '@/components/commons/radio'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Camera01Icon, Trash01Icon, UploadIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Spinner } from '@/components/ui/spinner'
import { Stepper } from '@/components/ui/stepper'
import { ListingTypeSelect } from '@/features/luxe-submissions/components/listing-type-select'
import { LuxePhotoUploadButton } from '@/features/luxe-submissions/components/luxe-photo-upload-button'
import { SubmissionFooter } from '@/features/luxe-submissions/components/submission-footer'
import { SubmissionFormHeader } from '@/features/luxe-submissions/components/submission-form-header'
import { SubmissionSuccess } from '@/features/luxe-submissions/components/submission-success'
import {
	MIN_LUXE_PHOTOS,
	OtherListingSchema
} from '@/features/luxe-submissions/form-schemas'
import type { SubmitListingResponse } from '@/features/luxe-submissions/http/luxe-submissions.http'
import { useDeleteLuxeGalleryImage } from '@/features/luxe-submissions/queries/use-delete-luxe-gallery-image'
import { useSubmitOtherListing } from '@/features/luxe-submissions/queries/use-submit-other-listing'
import { cn } from '@/helpers/utils'
import { type TKey, Trans, useTranslation } from '@/translations/i18n'

type PhotoSubmissionType = '' | 'url' | 'device'

function LuxeSubmissionsSubmissionOther() {
	const { t } = useTranslation()
	const [submittedData, setSubmittedData] =
		useState<SubmitListingResponse | null>(null)
	const [isImageLoading, setIsImageLoading] = useState(false)

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		formState: { errors }
	} = useForm({
		resolver: zodResolver(OtherListingSchema),
		defaultValues: {
			listingName: '',
			hostEmail: '',
			geo: '',
			listingUrl: undefined,
			bedrooms: 0,
			beds: 0,
			bathrooms: 0,
			guestCount: 0,
			photoSubmissionType: '',
			photoRepositoryUrl: undefined,
			galleryId: '',
			listingInventory: '',
			photos: []
		}
	})

	const [photoSubmissionType, photos, galleryId] = watch([
		'photoSubmissionType',
		'photos',
		'galleryId'
	])

	const urlSectionRef = useRef<HTMLDivElement>(null)
	const deviceSectionRef = useRef<HTMLDivElement>(null)
	const hasPhotos = photos && photos.length > 0
	const handlePhotoTypeChange = (type: PhotoSubmissionType) => {
		setValue('photos', [])
		setValue('galleryId', '')
		setValue('photoSubmissionType', type)
		requestAnimationFrame(() => {
			const ref = type === 'url' ? urlSectionRef : deviceSectionRef
			ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
		})
	}
	const deleteImageMutation = useDeleteLuxeGalleryImage({
		galleryId: galleryId ?? '',
		mutationOptions: {
			onSuccess: (_data, imageId) => {
				const photo = photos.find((p) => p.imageId === imageId)
				if (photo?.imageUrl.startsWith('blob:')) {
					URL.revokeObjectURL(photo.imageUrl)
				}
				setValue(
					'photos',
					photos.filter((p) => p.imageId !== imageId)
				)
			}
		}
	})

	const handleDeletePhoto = (photo: OtherListingSchema['photos'][number]) => {
		if (photo.uploading) {
			return
		}

		if (photo.failed) {
			// Failed photos aren't confirmed on server — remove locally
			if (photo.imageUrl.startsWith('blob:')) {
				URL.revokeObjectURL(photo.imageUrl)
			}
			setValue(
				'photos',
				photos.filter((p) => p.imageId !== photo.imageId)
			)
			return
		}

		deleteImageMutation.mutate(photo.imageId)
	}

	const { mutate: submitListing, isPending } = useSubmitOtherListing({
		onSuccess: (data) => {
			setSubmittedData(data)
		}
	})

	const onSubmit = handleSubmit((data) => {
		submitListing({
			...data,
			listingUrl: data.listingUrl ? data.listingUrl : undefined,
			photoRepositoryUrl: data.photoRepositoryUrl
				? data.photoRepositoryUrl
				: undefined,
			galleryId: data.galleryId ? data.galleryId : undefined,
			walkthroughContactEmail: data.walkthroughContactEmail
				? data.walkthroughContactEmail
				: undefined
		})
	})

	if (submittedData) {
		return <SubmissionSuccess submissionDetails={submittedData} />
	}

	return (
		<div className='flex flex-1 flex-col overflow-hidden bg-neutral-white'>
			<SubmissionFormHeader />

			<div className='flex flex-1 flex-col overflow-auto'>
				<form
					className='m:m-6 flex flex-col gap-6 m:rounded-3xl m:border m:border-neutral-300 m:py-6 py-4'
					id='submission-form'
					onSubmit={onSubmit}
				>
					<div className='flex flex-col gap-6 px-6'>
						<h2 className='font-bold text-lg text-neutral-900'>
							{t('luxe_submissions:submission_details')}
						</h2>
						<div className='grid grid-cols-1 m:grid-cols-2 gap-6'>
							<Controller
								control={control}
								name='listingUrl'
								render={({ field }) => (
									<InputText
										{...field}
										errorMessage={t(
											errors.listingUrl?.message as TKey
										)}
										helpText={t(
											'luxe_submissions:listing_url_external_hint'
										)}
										label={t(
											'luxe_submissions:listing_url_optional'
										)}
										placeholder={t(
											'luxe_submissions:listing_url_placeholder'
										)}
										type='text'
									/>
								)}
							/>
							<Controller
								control={control}
								name='listingName'
								render={({ field }) => (
									<InputText
										{...field}
										errorMessage={t(
											errors.listingName?.message as TKey
										)}
										label={t(
											'luxe_submissions:listing_name'
										)}
										placeholder={t(
											'luxe_submissions:listing_name_placeholder'
										)}
										type='text'
									/>
								)}
							/>

							<Controller
								control={control}
								name='listingInventory'
								render={({ field }) => {
									return (
										<ListingTypeSelect
											errorMessage={
												errors.listingInventory
													?.message as TKey
											}
											name={field.name}
											onChange={(val) =>
												field.onChange(val ?? '')
											}
											value={field.value ?? ''}
										/>
									)
								}}
							/>

							<Controller
								control={control}
								name='geo'
								render={({ field }) => (
									<InputText
										{...field}
										errorMessage={t(
											errors.geo?.message as TKey
										)}
										label={t('luxe_submissions:geo')}
										placeholder={t(
											'luxe_submissions:geo_placeholder'
										)}
										type='text'
									/>
								)}
							/>

							<Controller
								control={control}
								name='hostEmail'
								render={({ field }) => (
									<InputText
										{...field}
										errorMessage={t(
											errors.hostEmail?.message as TKey
										)}
										label={t('luxe_submissions:host_email')}
										placeholder={t(
											'luxe_submissions:host_email_placeholder'
										)}
										type='email'
									/>
								)}
							/>

							<Controller
								control={control}
								name='walkthroughContactEmail'
								render={({ field }) => (
									<InputText
										{...field}
										errorMessage={t(
											errors.walkthroughContactEmail
												?.message as TKey
										)}
										label={t(
											'luxe_submissions:walkthrough_contact_email'
										)}
										placeholder={t(
											'luxe_submissions:contact_email_placeholder'
										)}
										type='email'
									/>
								)}
							/>
						</div>
						<div className='flex flex-col gap-2'>
							<p className='font-medium text-base text-grey-750'>
								{t('luxe_submissions:property_details')}
							</p>
							<div className='m:flex grid grid-cols-2 gap-x-15 gap-y-4'>
								<Controller
									control={control}
									name='bedrooms'
									render={({ field }) => (
										<Stepper
											errorMessage={t(
												errors.bedrooms?.message as TKey
											)}
											label={t(
												'luxe_submissions:bedrooms_count'
											)}
											min={0}
											name={field.name}
											onChange={field.onChange}
											value={field.value}
										/>
									)}
								/>

								<Controller
									control={control}
									name='beds'
									render={({ field }) => (
										<Stepper
											errorMessage={t(
												errors.beds?.message as TKey
											)}
											label={t(
												'luxe_submissions:bed_count'
											)}
											min={0}
											name={field.name}
											onChange={field.onChange}
											value={field.value}
										/>
									)}
								/>

								<Controller
									control={control}
									name='bathrooms'
									render={({ field }) => (
										<Stepper
											errorMessage={t(
												errors.bathrooms
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:bathrooms_count'
											)}
											min={0}
											name={field.name}
											onChange={field.onChange}
											value={field.value}
										/>
									)}
								/>

								<Controller
									control={control}
									name='guestCount'
									render={({ field }) => (
										<Stepper
											errorMessage={t(
												errors.guestCount
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:guest_count'
											)}
											min={0}
											name={field.name}
											onChange={field.onChange}
											value={field.value}
										/>
									)}
								/>
							</div>
						</div>
					</div>

					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1 px-6'>
							<p className='font-medium text-base text-grey-750'>
								{t('luxe_submissions:photos')}
							</p>
							<p className='text-neutral-600 text-sm'>
								{t(
									'luxe_submissions:photos_required_description'
								)}
							</p>
						</div>

						<Controller
							control={control}
							name='photoSubmissionType'
							render={({ field, fieldState }) => (
								<div className='flex flex-col gap-4 px-6'>
									<Radio
										checked={field.value === 'url'}
										label={
											<div className='flex flex-col gap-0.5'>
												<span className='text-neutral-900'>
													{t(
														'luxe_submissions:photos_repository_url'
													)}
												</span>
												<span className='text-neutral-600 text-sm'>
													{t(
														'luxe_submissions:photos_repository_url_description'
													)}
												</span>
											</div>
										}
										name='photoSubmissionType'
										onChange={() =>
											handlePhotoTypeChange('url')
										}
										value='url'
										variant='brand'
									/>

									<Radio
										checked={field.value === 'device'}
										label={
											<div className='flex flex-col gap-0.5'>
												<span className='text-neutral-900'>
													{t(
														'luxe_submissions:photos_from_device'
													)}
												</span>
												<span className='text-neutral-600 text-sm'>
													{t(
														'luxe_submissions:photos_from_device_description',
														{
															minimumPhotos:
																MIN_LUXE_PHOTOS
														}
													)}
												</span>
											</div>
										}
										name='photoSubmissionType'
										onChange={() =>
											handlePhotoTypeChange('device')
										}
										value='device'
										variant='brand'
									/>

									{fieldState.error && (
										<p className='text-danger-600 text-sm'>
											{t('errors:field_required')}
										</p>
									)}
								</div>
							)}
						/>

						{photoSubmissionType === 'url' && (
							<div
								className='grid m:grid-cols-2 px-6'
								ref={urlSectionRef}
							>
								<Controller
									control={control}
									name='photoRepositoryUrl'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.photoRepositoryUrl
													?.message as TKey
											)}
											helpText={t(
												'luxe_submissions:photos_url_hint'
											)}
											label={t(
												'luxe_submissions:photos_url_label'
											)}
											placeholder={t(
												'luxe_submissions:photos_url_placeholder'
											)}
											type='text'
										/>
									)}
								/>
							</div>
						)}

						{photoSubmissionType === 'device' && (
							<div
								className='flex flex-col gap-3'
								ref={deviceSectionRef}
							>
								<div className='flex flex-col gap-1 px-6'>
									<p className='font-medium text-grey-750 text-sm'>
										{t('luxe_submissions:add_photos')}
									</p>
									<p className='text-neutral-600 text-sm'>
										{t('luxe_submissions:add_photos_hint')}
									</p>
								</div>

								{hasPhotos && (
									<div className='relative'>
										<ScrollArea
											className='w-full'
											scrollbar={
												<Scrollbar orientation='horizontal' />
											}
											viewportProps={{
												className: 'px-6'
											}}
										>
											<div className='flex gap-3'>
												{photos.map((photo, index) => (
													<div
														className='relative size-22.5 shrink-0 overflow-hidden rounded-lg'
														key={`${photo.imageId}-${index}`}
													>
														{photo.uploading && (
															<div className='absolute inset-0 z-1 flex items-center justify-center rounded-lg bg-neutral-black/30'>
																<Spinner className='size-5' />
															</div>
														)}
														{!photo.uploading && (
															<ButtonLegacy
																className='absolute top-2 right-2 bg-neutral-white/80'
																onClick={() =>
																	handleDeletePhoto(
																		photo
																	)
																}
																shape='square'
																size='sm'
																type='button'
																variant='secondary'
															>
																<Trash01Icon fill='neutral-900' />
															</ButtonLegacy>
														)}
														<img
															alt={`photo-${photo.imageId}`}
															className={cn(
																'size-full rounded-lg border object-cover',
																photo.failed
																	? 'border-2 border-red-700'
																	: 'border-neutral-black/20'
															)}
															height={90}
															src={photo.imageUrl}
															width={90}
														/>
													</div>
												))}
											</div>
										</ScrollArea>
										<div className='pointer-events-none absolute inset-y-0 left-0 w-6 bg-linear-to-r from-neutral-white to-transparent' />
										<div className='pointer-events-none absolute inset-y-0 right-0 w-6 bg-linear-to-l from-neutral-white to-transparent' />
									</div>
								)}

								<div className='flex items-center justify-between gap-4 px-6'>
									{(errors.photos?.message ||
										errors.galleryId?.message) && (
										<p className='text-danger-600 text-sm'>
											<Trans
												components={{ b: <strong /> }}
												i18nKey={
													(errors.photos?.message ||
														errors.galleryId
															?.message) as TKey
												}
												values={{
													needPhotosCount:
														MIN_LUXE_PHOTOS -
														photos.length
												}}
											/>
										</p>
									)}

									<LuxePhotoUploadButton
										className={
											hasPhotos
												? 'relative ms-auto inline-flex'
												: 'relative flex min-h-50 w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-xl border-2 border-neutral-300 border-dashed bg-neutral-50 px-6 py-10 text-center transition-colors'
										}
										galleryId={galleryId ?? ''}
										onGalleryIdChange={(id) =>
											setValue('galleryId', id)
										}
										onPhotosChange={(p) => {
											if (p.length >= MIN_LUXE_PHOTOS) {
												clearErrors('photos')
												clearErrors('galleryId')
											}
											setValue('photos', p)
										}}
										photos={photos}
										setIsLoading={setIsImageLoading}
									>
										{!hasPhotos && (
											<>
												<div className='mb-2 flex size-16 items-center justify-center rounded-full border border-neutral-200 bg-neutral-white'>
													<UploadIcon
														className='text-neutral-500'
														size={24}
													/>
												</div>

												<div className='flex flex-col gap-2'>
													<p className='font-bold text-base text-neutral-900'>
														{t(
															'luxe_submissions:photo_upload_drop_hint'
														)}
													</p>
													<p className='text-neutral-700 text-sm'>
														{t(
															'luxe_submissions:photo_upload_file_constraints',
															{
																minPhotosCount:
																	MIN_LUXE_PHOTOS
															}
														)}
													</p>
												</div>
											</>
										)}
										<ButtonLegacy
											className='bg-inherit'
											size='md'
											type='button'
											variant='outline'
										>
											<Camera01Icon
												data-color
												size={16}
											/>
											<span>
												{t(
													'luxe_submissions:add_photos_button'
												)}
											</span>
											{isImageLoading && <Spinner />}
										</ButtonLegacy>
									</LuxePhotoUploadButton>
								</div>
							</div>
						)}
					</div>
				</form>
			</div>

			<SubmissionFooter
				disabled={isPending}
				form='submission-form'
				isLoading={isPending}
				label={t('luxe_submissions:submit_listing')}
			/>
		</div>
	)
}

export { LuxeSubmissionsSubmissionOther }
